import { Link } from 'react-router-dom';
import { Wrench } from 'lucide-react';
import { formatUserDisplayName } from '../../lib/formatUserDisplayName';
import { centsToDisplay } from '../../lib/money';
import { storedConnectionForPlatform } from '../../lib/accountApiConnections';
import {
  describeSharedOn,
  type AdAccountSharingPlatform,
  type AdAccountSharingStatus,
} from '../../lib/adAccountSharing';
import type { AccountGridRow } from '../../types/accountGrid';
import { connectionPlatformForColumn, type AccountColumnKey } from './accountColumns';

// Renders one Accounts-grid cell. Extracted from AccountsGrid so the per-column
// rendering can be unit-tested in isolation (mirrors MasterGrid's body
// renderer). URLs render as truncated links; long text truncates with a
// title-tooltip; money uses the account's own currency. DERIVED API-connection
// columns read row.apiConnections (cache only — see AccountGridRow).

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' });
}

function statusBadge(status: string): string {
  switch (status) {
    case 'active':
      return 'bg-emerald-100 text-emerald-700';
    case 'paused':
      return 'bg-amber-100 text-amber-700';
    case 'cancelled':
      return 'bg-rose-100 text-rose-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

export function renderAccountCell(row: AccountGridRow, key: AccountColumnKey) {
  const a = row.account;

  // API connection status — read from the STORED block on the account.
  // Intercepted before the switch so the switch's default only ever indexes
  // real Account string fields. null = never checked (-> "—").
  const platform = connectionPlatformForColumn(key);
  if (platform) {
    return (
      <ConnCell
        value={storedConnectionForPlatform(a.apiConnections, platform)}
        checkedAt={a.apiConnections?.lastCheckedAt ?? null}
      />
    );
  }

  // DERIVED — shared-ad-account attribution status (§3b), read from the row
  // (resolved server-side over the whole roster). Intercepted before the switch
  // like the connection columns so the switch's default only indexes stored
  // string fields.
  if (key === 'adAccountSharing') {
    return (
      <AdAccountSharingCell status={row.adAccountSharing} sharedOn={row.adAccountSharedOn ?? []} />
    );
  }

  switch (key) {
    case 'name':
      return (
        <Link to={`/accounts/${a.id}`} className="font-medium text-slate-900 hover:underline">
          {a.name}
        </Link>
      );
    case 'accountType':
      return <span className="text-slate-700">{a.accountType}</span>;
    case 'status':
      return (
        <span className={`text-xs px-2 py-0.5 rounded-full ${statusBadge(a.status)}`}>{a.status}</span>
      );
    case 'accountManagerId':
      return row.accountManager ? (
        <span className="text-slate-700" title={row.accountManager.email}>
          {formatUserDisplayName(row.accountManager)}
        </span>
      ) : (
        <span className="text-slate-400">—</span>
      );
    case 'mediaSpendPaidBy':
      return <span className="text-slate-700">{a.mediaSpendPaidBy}</span>;
    case 'slug':
      return <span className="font-mono text-xs text-slate-600">{a.slug}</span>;
    case 'industry':
      return <span className="text-slate-700">{a.industry}</span>;
    case 'currency':
      return <span className="text-slate-700">{a.currency}</span>;
    case 'country':
      return <Text value={a.country ?? null} />;
    case 'parentAccountId':
      return a.parentAccountId ? (
        <Link to={`/accounts/${a.parentAccountId}`} className="text-slate-700 hover:underline">
          {row.parentAccountName ?? a.parentAccountId}
        </Link>
      ) : (
        <span className="text-slate-400">—</span>
      );
    case 'metaMasterMonthlyBudget':
      return <span className="tabular-nums">{centsToDisplay(a.metaMasterMonthlyBudget, a.currency)}</span>;
    case 'googleMasterMonthlyBudget':
      return <span className="tabular-nums">{centsToDisplay(a.googleMasterMonthlyBudget, a.currency)}</span>;
    case 'runsMeta':
      return <BoolCell value={a.runsMeta ?? true} />;
    case 'runsGoogle':
      return <BoolCell value={a.runsGoogle ?? true} />;
    case 'reportingSyncEnabled':
      // Opt-in (Build A): legacy/unset → false, so an un-enrolled account reads ✗.
      return <BoolCell value={a.reportingSyncEnabled ?? false} />;
    case 'services':
      return a.services.length > 0 ? (
        <span className="text-slate-600 text-xs">{a.services.join(', ')}</span>
      ) : (
        <span className="text-slate-400">—</span>
      );
    case 'zernioProfileId':
      return <CodeChip value={a.zernioProfileId} />;
    // Feature A defaults. Names are denormalised on the account (shown directly);
    // the wrench deep-links to Meta Setup for that account (same ?account=&tab=
    // mechanism the account-detail "Manage" links use).
    case 'defaultPixelName':
      return <Text value={a.defaultPixelName ?? null} />;
    case 'defaultLeadFormName':
      return (
        <span className="inline-flex items-center gap-1.5">
          <Text value={a.defaultLeadFormName ?? null} />
          <MetaSetupWrench accountId={a.id} tab="forms" label="Manage lead forms" />
        </span>
      );
    case 'defaultAudienceCount':
      return (
        <span className="inline-flex items-center gap-1.5">
          {row.defaultAudienceCount > 0 ? (
            <span className="tabular-nums text-slate-700">{row.defaultAudienceCount}</span>
          ) : (
            <span className="text-slate-400">—</span>
          )}
          <MetaSetupWrench accountId={a.id} tab="audiences" label="Manage audiences" />
        </span>
      );
    case 'fbAdAccountUrl':
    case 'googleAdsUrl':
    case 'websiteUrl':
    case 'facebookPageUrl':
    case 'instagramUrl':
    case 'tiktokUrl':
    case 'googleBusinessProfileUrl':
    case 'reportUrl':
    case 'clientDriveUrl':
    case 'internalDriveUrl':
      return <UrlCell url={a[key]} />;
    // The column KEY strings stay `billingAddress.*` (stable grid identifiers +
    // saved User.gridPreferences); only the VALUE read moves to `address`.
    case 'billingAddress.suburb':
      return <Text value={a.address?.suburb ?? null} />;
    case 'billingAddress.state':
      return <Text value={a.address?.state ?? null} />;
    case 'billingAddress.country':
      return <Text value={a.address?.country ?? null} />;
    case 'createdAt':
      return <span className="text-slate-500 text-xs">{fmtDate(a.createdAt)}</span>;
    case 'updatedAt':
      return <span className="text-slate-500 text-xs">{fmtDate(a.updatedAt)}</span>;
    case 'statusChangedAt':
      return <span className="text-slate-500 text-xs">{fmtDate(a.statusChangedAt)}</span>;
    case 'description':
    case 'notes':
    case 'targetingNotes':
    case 'leadSyncingNotes':
      return <Truncated value={a[key]} />;
    default:
      // fbAdAccountId, defaultInstagramAccountId, defaultInstagramAccountName,
      // googleAdsCustomerId, googleAnalyticsPropertyId, salesforceAccountId —
      // plain string fields (value shown, or "—" when null/empty).
      // (Connection keys are handled by the early return above; the record cast
      // keeps TS happy since `key` still nominally includes them here.)
      return <Text value={((a as Record<string, unknown>)[key] as string | null) ?? null} />;
  }
}

function BoolCell({ value }: { value: boolean }) {
  return value ? (
    <span className="text-emerald-600" aria-label="yes">✓</span>
  ) : (
    <span className="text-slate-300" aria-label="no">✗</span>
  );
}

// Stored (last-known) connection status. Three states: connected (✓), checked-
// but-not-connected (—), and never checked (—, distinct tooltip). The tooltip
// carries lastCheckedAt so "✓" reads honestly as "connected as of <date>".
function ConnCell({ value, checkedAt }: { value: boolean | null; checkedAt: string | null }) {
  const checked = checkedAt ? `checked ${fmtDate(checkedAt)}` : null;
  if (value === null) {
    return (
      <span className="text-slate-300" title="Never checked" aria-label="never checked">
        —
      </span>
    );
  }
  return value ? (
    <span
      className="text-emerald-600"
      title={checked ? `Connected (${checked})` : 'Connected'}
      aria-label="connected"
    >
      ✓
    </span>
  ) : (
    <span
      className="text-slate-400"
      title={checked ? `Not connected (${checked})` : 'Not connected'}
      aria-label="not connected"
    >
      —
    </span>
  );
}

// Shared-ad-account attribution status (§3b). The "shared — no match token" case
// is the gap to fix (its spend can't be attributed) and is styled amber to stand
// out, matching the connection-status column's use-colour-to-flag pattern. The
// other states are quiet (a token'd or unshared account needs no attention).
// §3b — platform-agnostic: the account may be shared on Meta, Google, or both.
// The tooltip NAMES the platform(s) so an AM knows which side the gap is on;
// match tokens are ONE list serving both, so a single token closes it either way.
function AdAccountSharingCell({
  status,
  sharedOn,
}: {
  status: AdAccountSharingStatus;
  sharedOn: readonly AdAccountSharingPlatform[];
}) {
  const on = describeSharedOn(sharedOn);
  // Defensive: a shared status always carries a platform, but a stale client/row
  // shouldn't render "Shares a  ad account".
  const where = on ? `a ${on} ad account` : 'an ad account';
  switch (status) {
    case 'shared-no-token':
      return (
        <span
          className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
          title={`Shares ${where} with other locations but has no match token — its spend can't be attributed until one is added.`}
        >
          Shared — no match token
        </span>
      );
    case 'shared-token-set':
      return (
        <span
          className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700"
          title={`Shares ${where}; a match token is set, so its spend can be attributed.`}
        >
          Shared — token set
        </span>
      );
    case 'not-shared':
      return <span className="text-xs text-slate-500">Not shared</span>;
    case 'no-ad-account':
      return <span className="text-slate-400" title="No ad account set">—</span>;
  }
}

function Text({ value }: { value: string | null }) {
  return value ? <span className="text-slate-700">{value}</span> : <span className="text-slate-400">—</span>;
}

function Truncated({ value }: { value: string | null }) {
  if (!value) return <span className="text-slate-400">—</span>;
  return (
    <span className="text-slate-600 block max-w-[240px] truncate" title={value}>
      {value}
    </span>
  );
}

// Copyable code chip (e.g. Zernio profile id). Click copies the raw value.
function CodeChip({ value }: { value: string | null }) {
  if (!value) return <span className="text-slate-400">—</span>;
  const copy = () => {
    void navigator.clipboard?.writeText(value);
  };
  return (
    <button
      type="button"
      onClick={copy}
      title="Click to copy"
      className="inline-flex max-w-[180px] items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-700 hover:bg-slate-200"
    >
      <span className="truncate">{value}</span>
    </button>
  );
}

// Wrench → Meta Setup, pre-selecting this account + tab. Reuses the Phase-0
// deep-link the account-detail "Manage" links use (?account=<id>&tab=...).
function MetaSetupWrench({
  accountId,
  tab,
  label,
}: {
  accountId: string;
  tab: 'forms' | 'audiences';
  label: string;
}) {
  return (
    <Link
      to={`/advertising/meta-setup?account=${encodeURIComponent(accountId)}&tab=${tab}`}
      title={label}
      aria-label={label}
      className="text-slate-400 hover:text-slate-700"
    >
      <Wrench className="h-3.5 w-3.5" aria-hidden />
    </Link>
  );
}

function UrlCell({ url }: { url: string | null }) {
  if (!url) return <span className="text-slate-400">—</span>;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="text-indigo-600 hover:underline block max-w-[200px] truncate"
      title={url}
    >
      {url}
    </a>
  );
}
